import { useState, useEffect, useRef, useCallback, useMemo, type ComponentType, type ReactNode } from 'react';
import {
  Cog6ToothIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
  BoltIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  LinkIcon,
  VideoCameraIcon,
  RectangleGroupIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import { Modal, ConfirmDialog, SegmentedToggle, SeverityTag, Tooltip } from '@trinetra/shared';
import {
  getNodeAlertConfigs,
  createNodeAlertConfig,
  updateNodeAlertConfig,
  toggleNodeAlertConfig,
  duplicateNodeAlertConfig,
  deleteNodeAlertConfig,
  getNodeCameras,
  getNodeGroups,
} from '../services/api.service';
import {
  EVENT_TYPE_OPTIONS,
  SEVERITY_OPTIONS,
  scopeOf,
  eventTypeLabel,
  parseEventTypes,
  type RuleScope,
} from '../constants/alertRules';

const blankConfig = () => ({
  name: '', event_type: '*', severity: '*', camera_id: null, group_id: null,
  min_confidence: 0.5, cooldown_seconds: 30, description: '',
  email_enabled: false, email_recipients: '', sms_enabled: false, sms_recipients: '',
  webhook_enabled: false, webhook_url: '', websocket_enabled: true, is_active: true,
});

/** Normalize form → API payload: clamp confidence, coerce numbers, enforce scope exclusivity. */
function normalize(form: any) {
  const scope = scopeOf(form);
  return {
    ...form,
    // Empty selection means "all event types" — persist the wildcard so an empty rule
    // isn't silently dead (an empty event_type matches nothing at evaluation time).
    event_type: parseEventTypes(form.event_type).join(',') || '*',
    camera_id: scope === 'camera' ? form.camera_id : null,
    group_id: scope === 'group' ? form.group_id : null,
    min_confidence: Math.min(1, Math.max(0, parseFloat(form.min_confidence) || 0)),
    cooldown_seconds: Math.max(0, parseInt(form.cooldown_seconds, 10) || 0),
  };
}

/** The message an operator can act on, out of whatever shape the failure arrived in. */
function errorText(err: any, fallback: string): string {
  return (
    err?.response?.data?.error?.message ||
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
}

type StatusFilter = 'all' | 'enabled' | 'disabled';

export default function AnalyticsConfigPage() {
  const { selectedNode } = useNode();
  const { hasPermission } = useAuth();
  // Creating / editing / toggling / duplicating / deleting alert rules is gated
  // on alerts.config; the backend enforces it too. View stays open to any viewer.
  const canConfig = hasPermission('alerts.config');
  const nodeId = selectedNode?.nodeId || '';

  const [configs, setConfigs] = useState<any[]>([]);
  const [cameras, setCameras] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');

  // One modal serves both create and edit: `null` = closed, `{ rule: null }` = new rule.
  // Editing used to expand the row into an inline form, which pushed every rule below it off
  // screen and gave a half-finished edit nowhere to live but the list.
  const [formFor, setFormFor] = useState<{ rule: any | null } | null>(null);
  const [deleteFor, setDeleteFor] = useState<any | null>(null);
  // Rules whose enable/disable call is in flight — keyed by id so one pending toggle can't
  // freeze the whole table.
  const [togglingIds, setTogglingIds] = useState<string[]>([]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const fetchConfigs = useCallback(async () => {
    if (!nodeId) { setConfigs([]); setLoading(false); return; }
    setLoading(true);
    try {
      setConfigs(await getNodeAlertConfigs(nodeId) || []);
      setPageError('');
    } catch (err) {
      console.error('Failed to fetch configs:', err);
      setPageError(errorText(err, 'Failed to load alert rules'));
    } finally {
      setLoading(false);
    }
  }, [nodeId]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  /**
   * A rule form (or a delete confirmation) open across a site switch is pointed at a rule id
   * belonging to the site just left — its Save would write to the new node under an id that
   * node has never issued. The rules list refetches above; these have to be closed.
   */
  const firstNodeRef = useRef(true);
  useEffect(() => {
    if (firstNodeRef.current) { firstNodeRef.current = false; return; }
    setFormFor(null);
    setDeleteFor(null);
    setTogglingIds([]);
    setPageError('');
  }, [nodeId]);

  // Cameras + groups feed the scope pickers in the rule form.
  useEffect(() => {
    if (!nodeId) return;
    getNodeCameras(nodeId).then((c: any) => setCameras(Array.isArray(c) ? c : c?.data || [])).catch(() => setCameras([]));
    getNodeGroups(nodeId).then((g: any) => setGroups(Array.isArray(g) ? g : g?.data || [])).catch(() => setGroups([]));
  }, [nodeId]);

  const handleToggle = async (cfg: any) => {
    setTogglingIds((prev) => [...prev, cfg.id]);
    // Optimistic: the switch is the one control an operator flips repeatedly, and waiting a
    // tunnel round trip for it to move reads as a dead toggle. Reverted below if it fails.
    setConfigs((prev) => prev.map((c) => c.id === cfg.id ? { ...c, is_active: !c.is_active } : c));
    try {
      const updated = await toggleNodeAlertConfig(nodeId, cfg.id);
      setConfigs((prev) => prev.map((c) => c.id === cfg.id ? { ...c, is_active: updated.is_active } : c));
      setPageError('');
    } catch (err) {
      console.error('Toggle failed:', err);
      setConfigs((prev) => prev.map((c) => c.id === cfg.id ? { ...c, is_active: cfg.is_active } : c));
      setPageError(errorText(err, `Failed to ${cfg.is_active ? 'disable' : 'enable'} this rule`));
    } finally {
      setTogglingIds((prev) => prev.filter((id) => id !== cfg.id));
    }
  };

  const handleDelete = async () => {
    if (!deleteFor) return;
    try {
      await deleteNodeAlertConfig(nodeId, deleteFor.id);
      setConfigs((prev) => prev.filter((c) => c.id !== deleteFor.id));
      setDeleteFor(null);
      setPageError('');
    } catch (err) {
      console.error('Delete failed:', err);
      setDeleteFor(null);
      setPageError(errorText(err, 'Failed to delete this rule'));
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      const clone = await duplicateNodeAlertConfig(nodeId, id);
      setConfigs((prev) => [clone, ...prev]);
      setPageError('');
    } catch (err) {
      console.error('Duplicate failed:', err);
      setPageError(errorText(err, 'Failed to duplicate this rule'));
    }
  };

  // Both submit paths throw on failure: the modal stays open and shows why, instead of
  // closing on a save that never happened.
  const handleSubmit = async (data: any) => {
    if (formFor?.rule) {
      const updated = await updateNodeAlertConfig(nodeId, formFor.rule.id, normalize(data));
      setConfigs((prev) => prev.map((c) => c.id === formFor.rule.id ? updated : c));
    } else {
      const created = await createNodeAlertConfig(nodeId, normalize(data));
      setConfigs((prev) => [created, ...prev]);
    }
    setPageError('');
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return configs.filter((c) => {
      if (statusFilter === 'enabled' && !c.is_active) return false;
      if (statusFilter === 'disabled' && c.is_active) return false;
      if (!q) return true;
      // Searches what's on screen: the rule's own words plus the event types it targets.
      return [c.name, c.description, eventTypeLabel(c.event_type), c.severity]
        .filter(Boolean)
        .some((v: any) => String(v).toLowerCase().includes(q));
    });
  }, [configs, search, statusFilter]);

  const enabledCount = configs.filter((c) => c.is_active).length;

  if (!nodeId) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground p-6">
        Select a site to configure its alert rules.
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-6">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <Cog6ToothIcon className="w-6 h-6 text-brand shrink-0" />
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-foreground">Alert Rules</h1>
            <p className="text-xs text-muted-foreground">
              Decide which AI detections on <span className="font-medium">{selectedNode?.name || nodeId}</span> become alerts.
            </p>
          </div>
          <span className="text-[12px] text-muted-foreground whitespace-nowrap">
            {configs.length} {configs.length === 1 ? 'rule' : 'rules'} · {enabledCount} enabled
          </span>
        </div>
        <div className="flex items-center gap-2">
          {canConfig && (
            <button
              onClick={() => setFormFor({ rule: null })}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand text-white text-sm hover:opacity-90"
            >
              <PlusIcon className="w-4 h-4" /> New Rule
            </button>
          )}
          <Tooltip label="Reload rules" side="bottom">
            <button onClick={fetchConfigs} className="p-2 rounded-lg hover:bg-muted text-muted-foreground" aria-label="Reload rules">
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* ── Filters: a site with 40 rules is a list you scan, not one you read ── */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search rules, event types…"
            className="w-64 rounded-lg border border-border bg-background py-1.5 pl-8 pr-2 text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <SegmentedToggle<StatusFilter>
          aria-label="Filter rules by state"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { key: 'all', label: 'All' },
            { key: 'enabled', label: 'Enabled' },
            { key: 'disabled', label: 'Disabled' },
          ]}
        />
        {(search || statusFilter !== 'all') && (
          <span className="text-[12px] text-muted-foreground">
            {filtered.length} of {configs.length} shown
          </span>
        )}
      </div>

      {pageError && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-[13px] text-destructive">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="flex-1">{pageError}</span>
          <button onClick={() => setPageError('')} className="text-[12px] underline opacity-80 hover:opacity-100">Dismiss</button>
        </div>
      )}

      {/* ── The rules ── */}
      <div className="flex-1 min-h-0 overflow-hidden rounded-xl border border-border bg-card">
        <RuleTable
          rules={filtered}
          totalCount={configs.length}
          cameras={cameras}
          groups={groups}
          canConfig={canConfig}
          loading={loading}
          togglingIds={togglingIds}
          onToggle={handleToggle}
          onEdit={(rule: any) => setFormFor({ rule })}
          onDuplicate={handleDuplicate}
          onDelete={setDeleteFor}
          onCreate={() => setFormFor({ rule: null })}
          onClearFilters={() => { setSearch(''); setStatusFilter('all'); }}
        />
      </div>

      {/* ── Create / edit, same form either way ── */}
      <RuleFormModal
        open={!!formFor}
        rule={formFor?.rule || null}
        cameras={cameras}
        groups={groups}
        onSubmit={handleSubmit}
        onClose={() => setFormFor(null)}
      />

      <ConfirmDialog
        open={!!deleteFor}
        title="Delete alert rule"
        message={
          <>
            Detections matching <span className="font-medium text-foreground">{ruleTitle(deleteFor)}</span> will
            no longer raise alerts. Alerts already raised are kept.
          </>
        }
        confirmLabel="Delete rule"
        onConfirm={handleDelete}
        onCancel={() => setDeleteFor(null)}
      />
    </div>
  );
}

// ─── Row vocabulary ───────────────────────────────────────────

function ruleTitle(config: any): string {
  if (!config) return '';
  return config.name || `Rule: ${eventTypeLabel(config.event_type)}`;
}
const cameraName = (cams: any[], id: string) => cams.find((c) => c.id === id)?.name || id;
const groupName = (grps: any[], id: string) => grps.find((g) => g.id === id)?.name || id;

/** Scope as icon + text, so the column can be read down rather than word by word. */
function ScopeCell({ config, cameras, groups }: { config: any; cameras: any[]; groups: any[] }) {
  const scope = scopeOf(config);
  const Icon = scope === 'camera' ? VideoCameraIcon : scope === 'group' ? RectangleGroupIcon : GlobeAltIcon;
  const text = scope === 'camera'
    ? cameraName(cameras, config.camera_id)
    : scope === 'group'
      ? groupName(groups, config.group_id)
      : 'All cameras';
  return (
    <div className="flex min-w-0 items-center gap-1.5" title={text}>
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate text-foreground/90">{text}</span>
    </div>
  );
}

/**
 * Event types as chips, capped at two.
 *
 * A rule can target a dozen types; spelling them all out made one row four lines tall and the
 * table unscannable, so the rest collapse into a count that carries the full list on hover.
 */
function EventTypesCell({ value }: { value?: string | null }) {
  const tokens = parseEventTypes(value);
  if (tokens.length === 0 || tokens.includes('*')) {
    return <span className="text-[12px] text-muted-foreground">All event types</span>;
  }
  const shown = tokens.slice(0, 2);
  const rest = tokens.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-1" title={eventTypeLabel(value)}>
      {shown.map((t) => (
        <span key={t} className="max-w-[10rem] truncate rounded bg-brand/15 px-1.5 py-0.5 text-[11px] text-brand">
          {eventTypeLabel(t)}
        </span>
      ))}
      {rest > 0 && (
        <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">+{rest}</span>
      )}
    </div>
  );
}

const NOTIFY_CHANNELS: { key: string; icon: ComponentType<{ className?: string }>; label: string; tone: string }[] = [
  { key: 'websocket_enabled', icon: BoltIcon, label: 'Live (in-app)', tone: 'text-emerald-500' },
  { key: 'email_enabled', icon: EnvelopeIcon, label: 'Email', tone: 'text-blue-400' },
  { key: 'sms_enabled', icon: DevicePhoneMobileIcon, label: 'SMS', tone: 'text-teal-400' },
  { key: 'webhook_enabled', icon: LinkIcon, label: 'Webhook', tone: 'text-purple-400' },
];

/**
 * All four channels in fixed positions, lit or faint.
 *
 * Rendering only the enabled ones (what the card list did) shifted the icons from row to row,
 * so "does this rule email anyone?" meant reading every badge instead of glancing down one
 * column.
 */
function NotifyCell({ config }: { config: any }) {
  return (
    <div className="flex items-center gap-1.5">
      {NOTIFY_CHANNELS.map(({ key, icon: Icon, label, tone }) => {
        const on = key === 'websocket_enabled' ? config[key] !== false : !!config[key];
        return (
          <Tooltip key={key} label={`${label} — ${on ? 'on' : 'off'}`} side="top">
            <span className={on ? tone : 'text-muted-foreground opacity-25'}>
              <Icon className="h-4 w-4" />
            </span>
          </Tooltip>
        );
      })}
    </div>
  );
}

/** Enable/disable switch. Read-only rendering for a viewer without alerts.config. */
function EnableSwitch({ checked, busy, disabled, label, onChange }: {
  checked?: boolean; busy?: boolean; disabled?: boolean; label: string; onChange: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      disabled={disabled || busy}
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-brand' : 'bg-muted border border-border'
      } ${disabled ? 'cursor-default opacity-70' : 'cursor-pointer'} ${busy ? 'opacity-60' : ''}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-[1.15rem]' : 'translate-x-[0.15rem]'
        }`}
      />
    </button>
  );
}

const TH = 'px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap';

function RuleTable({
  rules, totalCount, cameras, groups, canConfig, loading, togglingIds,
  onToggle, onEdit, onDuplicate, onDelete, onCreate, onClearFilters,
}: any) {
  if (loading) {
    return (
      <div className="space-y-2 p-4">
        {[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-muted" />)}
      </div>
    );
  }

  if (rules.length === 0) {
    // Two different empties: nothing configured yet, versus filters that hide everything.
    const filtered = totalCount > 0;
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
        <Cog6ToothIcon className="h-8 w-8 text-muted-foreground opacity-40" />
        <p className="text-sm font-medium text-foreground">
          {filtered ? 'No rules match these filters' : 'No alert rules yet'}
        </p>
        <p className="text-[13px] text-muted-foreground">
          {filtered
            ? 'Clear the search or state filter to see the rest.'
            : 'Until a rule matches it, an AI detection is recorded but nobody is told.'}
        </p>
        {filtered ? (
          <button onClick={onClearFilters} className="mt-1 text-sm text-brand">Clear filters</button>
        ) : canConfig ? (
          <button onClick={onCreate} className="mt-1 text-sm text-brand">Create your first rule</button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto">
      <table className="w-full min-w-[64rem] table-fixed text-[13px]">
        <colgroup>
          <col className="w-[24%]" />
          <col className="w-[18%]" />
          <col className="w-[14%]" />
          <col className="w-[9%]" />
          <col className="w-[11%]" />
          <col className="w-[9%]" />
          <col className="w-[7%]" />
          <col className="w-[8%]" />
        </colgroup>
        <thead className="sticky top-0 z-10">
          <tr className="border-b border-border bg-muted/60 backdrop-blur">
            <th className={`${TH} pl-4`}>Rule</th>
            <th className={TH}>Event types</th>
            <th className={TH}>Applies to</th>
            <th className={TH}>Severity</th>
            <th className={TH}>Threshold</th>
            <th className={TH}>Notify</th>
            <th className={TH}>Enabled</th>
            <th className={`${TH} pr-4 text-right`}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rules.map((cfg: any) => {
            const busy = togglingIds.includes(cfg.id);
            return (
              <tr
                key={cfg.id}
                onClick={canConfig ? () => onEdit(cfg) : undefined}
                className={`border-b border-border transition-colors last:border-0 hover:bg-muted/40 ${
                  canConfig ? 'cursor-pointer' : ''
                } ${cfg.is_active ? '' : 'opacity-60'}`}
              >
                <td className="py-2.5 pl-4 pr-3">
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-foreground" title={ruleTitle(cfg)}>
                      {ruleTitle(cfg)}
                    </div>
                    {cfg.description && (
                      <div className="mt-0.5 truncate text-[12px] text-muted-foreground" title={cfg.description}>
                        {cfg.description}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2.5"><EventTypesCell value={cfg.event_type} /></td>
                <td className="px-3 py-2.5"><ScopeCell config={cfg} cameras={cameras} groups={groups} /></td>
                <td className="px-3 py-2.5">
                  {cfg.severity && cfg.severity !== '*'
                    ? <SeverityTag severity={cfg.severity} variant="soft" dot={false} />
                    : <span className="text-[12px] text-muted-foreground">Auto</span>}
                </td>
                <td className="px-3 py-2.5">
                  <div className="text-foreground/90">≥ {((cfg.min_confidence || 0) * 100).toFixed(0)}%</div>
                  <div className="text-[12px] text-muted-foreground">{cfg.cooldown_seconds ?? 0}s cooldown</div>
                </td>
                <td className="px-3 py-2.5"><NotifyCell config={cfg} /></td>
                <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <EnableSwitch
                    checked={cfg.is_active}
                    busy={busy}
                    disabled={!canConfig}
                    label={`${cfg.is_active ? 'Disable' : 'Enable'} ${ruleTitle(cfg)}`}
                    onChange={() => onToggle(cfg)}
                  />
                </td>
                <td className="px-3 py-2.5 pr-4" onClick={(e) => e.stopPropagation()}>
                  {canConfig ? (
                    <div className="flex items-center justify-end gap-0.5">
                      <Tooltip label="Edit rule" side="top">
                        <button onClick={() => onEdit(cfg)} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Edit rule">
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip label="Duplicate rule" side="top">
                        <button onClick={() => onDuplicate(cfg.id)} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Duplicate rule">
                          <DocumentDuplicateIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip label="Delete rule" side="top">
                        <button onClick={() => onDelete(cfg)} className="rounded p-1.5 text-red-400 hover:bg-red-500/10" aria-label="Delete rule">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                    </div>
                  ) : (
                    <div className="text-right text-[12px] text-muted-foreground">—</div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── The form ─────────────────────────────────────────────────

const inputCls = 'mt-0.5 w-full px-2 py-1.5 text-sm rounded-lg border border-border bg-background text-foreground';
const labelCls = 'text-xs text-muted-foreground';

/**
 * Multi-select for event types. Stores a comma-separated string in `event_type`
 * (e.g. "crossing,loitering,person"), matched as OR at alert-evaluation time.
 * Pick from the known list, or type a custom type the AI pipeline emits.
 */
function EventTypeMultiSelect({ value, onChange }: { value?: string | null; onChange: (v: string) => void }) {
  const [custom, setCustom] = useState('');
  const selected = parseEventTypes(value);

  const commit = (next: string[]) => {
    const uniq = [...new Set(next.map((t) => String(t).trim()).filter(Boolean))];
    // '*' (all) is exclusive: it can't coexist with specific types.
    const cleaned = uniq.includes('*') && uniq.length > 1 ? uniq.filter((t) => t !== '*') : uniq;
    onChange(cleaned.join(','));
  };
  const add = (v: string) => {
    const t = String(v || '').trim();
    if (!t) return;
    if (t === '*') return commit(['*']);
    commit([...selected.filter((x) => x !== '*'), t]);
  };
  const remove = (v: string) => commit(selected.filter((t) => t !== v));

  const available = EVENT_TYPE_OPTIONS.filter((o) => !selected.includes(o.value));

  return (
    <div>
      <div className="mt-0.5 flex flex-wrap items-center gap-1 rounded-lg border border-border bg-background px-2 py-1.5 min-h-[38px]">
        {selected.length === 0 && <span className="text-sm text-muted-foreground">* (all event types)</span>}
        {selected.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded bg-brand/15 text-brand text-xs px-1.5 py-0.5">
            {eventTypeLabel(t)}
            <button type="button" onClick={() => remove(t)} className="leading-none hover:text-red-400" title="Remove">×</button>
          </span>
        ))}
      </div>
      <div className="mt-1 flex gap-1">
        <select value="" onChange={(e) => add(e.target.value)} className={inputCls}>
          <option value="" disabled>Add event type…</option>
          {available.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <input
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(custom); setCustom(''); } }}
          className={inputCls}
          placeholder="or type custom…"
        />
        <button type="button" onClick={() => { add(custom); setCustom(''); }} className="shrink-0 px-3 rounded-lg border border-border text-sm text-muted-foreground hover:bg-muted">Add</button>
      </div>
    </div>
  );
}

/** Notification channel: the switch and its recipients live on one line. */
function ChannelRow({ enabled, onToggle, icon: Icon, label, children }: {
  enabled: boolean;
  onToggle: (v: boolean) => void;
  icon: ComponentType<{ className?: string }>;
  label: string;
  children?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border p-2.5">
      <label className="flex items-center gap-2 text-sm text-foreground">
        <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)} className="accent-brand" />
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">{label}</span>
      </label>
      {children && <div className={enabled ? 'mt-2' : 'mt-2 opacity-50'}>{children}</div>}
    </div>
  );
}

/** Everything a rule decides, in the order it decides it: what matches, then who hears about it. */
function ConfigForm({ form, setForm, cameras, groups }: any) {
  const set = (key: string, val: any) => setForm({ ...form, [key]: val });
  const scope: RuleScope = scopeOf(form);

  const setScope = (next: RuleScope) => {
    if (next === 'all') setForm({ ...form, camera_id: null, group_id: null });
    else if (next === 'camera') setForm({ ...form, group_id: null, camera_id: form.camera_id || cameras[0]?.id || '' });
    else setForm({ ...form, camera_id: null, group_id: form.group_id || groups[0]?.id || '' });
  };

  const confidencePct = Math.round((parseFloat(form.min_confidence) || 0) * 100);

  return (
    <div className="space-y-5 p-5">
      {/* ── Detection: decides whether an event is an alert ── */}
      <section>
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Detection rule</h4>
        <p className="text-[11px] text-muted-foreground mb-2">Matching detections become alerts. The most specific rule wins (camera &gt; group &gt; all).</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block col-span-2">
            <span className={labelCls}>Rule name</span>
            <input value={form.name || ''} onChange={(e) => set('name', e.target.value)} className={inputCls} placeholder="e.g. Loading dock intrusion" />
          </label>

          <div className="block col-span-2">
            <span className={labelCls}>Event types</span>
            <EventTypeMultiSelect value={form.event_type} onChange={(v: string) => set('event_type', v)} />
            <p className="text-[11px] text-muted-foreground mt-1">Add one or more types — the rule fires if a detection matches any of them. Leave empty for all types.</p>
          </div>

          <label className="block">
            <span className={labelCls}>Applies to</span>
            <select value={scope} onChange={(e) => setScope(e.target.value as RuleScope)} className={inputCls}>
              <option value="all">All cameras</option>
              <option value="camera">Specific camera</option>
              <option value="group">Specific group</option>
            </select>
          </label>

          {scope === 'camera' && (
            <label className="block">
              <span className={labelCls}>Camera</span>
              <select value={form.camera_id || ''} onChange={(e) => set('camera_id', e.target.value)} className={inputCls}>
                <option value="" disabled>Select camera…</option>
                {cameras.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          )}
          {scope === 'group' && (
            <label className="block">
              <span className={labelCls}>Group</span>
              <select value={form.group_id || ''} onChange={(e) => set('group_id', e.target.value)} className={inputCls}>
                <option value="" disabled>Select group…</option>
                {groups.map((g: any) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </label>
          )}
          {scope === 'all' && <div />}

          <label className="block">
            <span className={labelCls}>Severity</span>
            <select value={form.severity || '*'} onChange={(e) => set('severity', e.target.value)} className={inputCls}>
              {SEVERITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>

          <label className="block">
            <span className={labelCls}>Cooldown (seconds)</span>
            <input type="number" min={0} value={form.cooldown_seconds ?? 0} onChange={(e) => set('cooldown_seconds', e.target.value)} className={inputCls} />
          </label>

          <label className="block col-span-2">
            <span className={labelCls}>Min confidence: <span className="text-foreground font-medium">{confidencePct}%</span></span>
            <input type="range" min={0} max={100} step={5} value={confidencePct}
              onChange={(e) => set('min_confidence', (parseInt(e.target.value, 10) || 0) / 100)}
              className="mt-2 w-full accent-brand" />
          </label>

          <label className="block col-span-2">
            <span className={labelCls}>Description (shown as the alert text)</span>
            <input value={form.description || ''} onChange={(e) => set('description', e.target.value)} className={inputCls} placeholder="Optional — defaults to an auto-generated summary" />
          </label>
        </div>
      </section>

      {/* ── Notifications ── */}
      <section>
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Notifications</h4>
        <div className="grid grid-cols-2 gap-2">
          <ChannelRow
            enabled={form.websocket_enabled !== false}
            onToggle={(v) => set('websocket_enabled', v)}
            icon={BoltIcon}
            label="Live (in-app)"
          >
            <p className="text-[11px] text-muted-foreground">Alert boards and the bell, in real time.</p>
          </ChannelRow>
          <ChannelRow
            enabled={!!form.email_enabled}
            onToggle={(v) => set('email_enabled', v)}
            icon={EnvelopeIcon}
            label="Email"
          >
            <input value={form.email_recipients || ''} onChange={(e) => set('email_recipients', e.target.value)} disabled={!form.email_enabled}
              className={`${inputCls} mt-0 disabled:opacity-50`} placeholder="a@x.com, b@y.com" />
          </ChannelRow>
          <ChannelRow
            enabled={!!form.sms_enabled}
            onToggle={(v) => set('sms_enabled', v)}
            icon={DevicePhoneMobileIcon}
            label="SMS"
          >
            <input value={form.sms_recipients || ''} onChange={(e) => set('sms_recipients', e.target.value)} disabled={!form.sms_enabled}
              className={`${inputCls} mt-0 disabled:opacity-50`} placeholder="+1..., +1..." />
          </ChannelRow>
          <ChannelRow
            enabled={!!form.webhook_enabled}
            onToggle={(v) => set('webhook_enabled', v)}
            icon={LinkIcon}
            label="Webhook"
          >
            <input value={form.webhook_url || ''} onChange={(e) => set('webhook_url', e.target.value)} disabled={!form.webhook_enabled}
              className={`${inputCls} mt-0 disabled:opacity-50`} placeholder="https://..." />
          </ChannelRow>
        </div>
      </section>
    </div>
  );
}

/** What can't be saved, said in the operator's terms rather than the API's. */
function validate(form: any): string {
  const scope = scopeOf(form);
  if (scope === 'camera' && !form.camera_id) return 'Pick the camera this rule applies to.';
  if (scope === 'group' && !form.group_id) return 'Pick the group this rule applies to.';
  if (form.email_enabled && !String(form.email_recipients || '').trim()) return 'Add at least one email recipient, or turn Email off.';
  if (form.sms_enabled && !String(form.sms_recipients || '').trim()) return 'Add at least one SMS number, or turn SMS off.';
  if (form.webhook_enabled && !String(form.webhook_url || '').trim()) return 'Add the webhook URL, or turn Webhook off.';
  return '';
}

/**
 * Create AND edit. One form for both, so the two can't drift, and an edit no longer expands
 * the row it belongs to — which pushed the rest of the table off screen mid-edit.
 */
function RuleFormModal({ open, rule, cameras, groups, onSubmit, onClose }: {
  open: boolean;
  rule: any | null;
  cameras: any[];
  groups: any[];
  onSubmit: (data: any) => Promise<void>;
  onClose: () => void;
}) {
  const [form, setForm] = useState<any>(blankConfig);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset on open, and whenever the modal is pointed at a different rule.
  useEffect(() => {
    if (!open) return;
    setForm(rule ? { ...rule } : blankConfig());
    setError('');
    setSaving(false);
  }, [open, rule]);

  const problem = validate(form);

  async function handleSave() {
    if (problem) { setError(problem); return; }
    setSaving(true);
    setError('');
    try {
      await onSubmit(form);
      onClose();
    } catch (err) {
      console.error('Save failed:', err);
      setError(errorText(err, 'Failed to save this rule'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={rule ? 'Edit alert rule' : 'New alert rule'}
      subtitle={rule ? ruleTitle(rule) : 'Which detections become alerts, and who hears about them.'}
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="min-w-0 flex-1 truncate text-[12px] text-destructive">{error}</span>
          <div className="flex shrink-0 items-center gap-2">
            <button onClick={onClose} disabled={saving} className="rounded-lg border border-border px-4 py-2 text-[13px] text-muted-foreground hover:bg-muted disabled:opacity-50">
              Cancel
            </button>
            <Tooltip label={problem || ''} side="top">
              <button
                onClick={handleSave}
                disabled={saving || !!problem}
                className="flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                {saving && <span className="h-3.5 w-3.5 animate-spin rounded-full border-t-2 border-white" />}
                {saving ? 'Saving…' : rule ? 'Save changes' : 'Create rule'}
              </button>
            </Tooltip>
          </div>
        </div>
      }
    >
      <ConfigForm form={form} setForm={setForm} cameras={cameras} groups={groups} />
    </Modal>
  );
}
